import React from 'react';
import { Pressable, Text, View, StyleSheet, useWindowDimensions } from 'react-native';
import { useDispatch } from 'react-redux';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { usePlannedMealTitle } from '../plannedMealTitle';
import { Paprika, PaprikaLayout } from '../../../../styles/theme';
import { TimeChip } from '../primitives/TimeChip';
import { VerdictAvatars } from '../../../household/VerdictAvatars';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { PlannedMeal } from '../../../../redux/mealPlanSlice';
import { acceptMeal } from '../../../../redux/mealPlanSlice';
import { ApprovalThumbs } from '../../ApprovalThumbs';
import { useHouseholdMembers } from '../../../../hooks/useHouseholdMembers';
import { canAcceptProposal, canRemoveProposal } from '../../proposalState';
import { nameForViewer } from '../../../../services/householdService';
import { RemoteImage } from '../../../shared/RemoteImage';

interface Props {
  recipe: RecipeIndexEntry;
  meal: PlannedMeal;
  servings?: number;
  isCooked?: boolean;
  /** Opens the recipe detail sheet (which owns Remplacer / Retirer). */
  onPress: () => void;
  /** Removes this meal from the slot. */
  onRemove?: () => void;
}

/**
 * Compact card for the 2nd and 3rd dish of a meal slot. The first dish keeps
 * the full-width photo card (MealRow); the extras stack underneath in this
 * shorter form so a two-dish dinner costs ~70px instead of a second 230px card.
 *
 * When the meal is a proposal (mode === 'proposed'), shows a special treatment:
 * dashed border, author tag, approval thumbs, and a "Put on menu" button.
 * Gated on status === 'paired' (solo users don't see proposals).
 */
export function SecondaryMealRow({ recipe, meal, servings, isCooked, onPress, onRemove }: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const title = usePlannedMealTitle(recipe, 'mobile-secondary');
  const { status, myUid, roster } = useHouseholdMembers();

  const isProposal = meal.mode === 'proposed' && status === 'paired';
  const canAccept = isProposal && canAcceptProposal({ myUid, cookId: meal.cookId });
  const canRemove = isProposal && canRemoveProposal({ proposal: meal, myUid, cookId: meal.cookId });

  const handleAccept = (e: any) => {
    e.stopPropagation?.();
    if (!myUid) return;
    dispatch(acceptMeal({ mealId: meal.id, uid: myUid }));
  };

  // Steps up with the card on tablets, same breakpoint as the hero circle (#632).
  const { width } = useWindowDimensions();
  const thumbSize = width >= PaprikaLayout.tabletMinWidth ? 60 : 46;
  const thumbStyle = { width: thumbSize, height: thumbSize };

  const handleRemove = (e: any) => {
    e.stopPropagation?.();
    onRemove?.();
  };

  if (isProposal) {
    return (
      <Pressable style={[styles.card, styles.proposalCard]} onPress={onPress}>
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={[styles.thumb, thumbStyle]}
          resizeMode="cover"
          testID="secondary-meal-thumb"
          fallback={<View style={[styles.thumb, thumbStyle, styles.thumbPlaceholder]} testID="secondary-meal-thumb" />}
        />

        <View style={styles.info}>
          <Text style={styles.proposalAuthor}>
            {t('household.approval.proposedBy', {
              name: meal.createdByUid
                ? nameForViewer(meal.createdByUid, myUid, roster, t('household.approval.self'))
                : '?',
            })}
          </Text>
          <Text style={styles.title} numberOfLines={2} testID="planned-meal-title">{title}</Text>
          <View style={styles.metaRow}>
            {recipe.readyInMinutes != null && <TimeChip minutes={recipe.readyInMinutes} />}
            {servings != null && (
              <View style={styles.servings}>
                <Feather name="users" size={11} color={Paprika.textMuted} />
                <Text style={styles.servingsText}>{servings}</Text>
              </View>
            )}
            {isCooked && <Text style={styles.cooked}>{t('thisWeek.cooked')}</Text>}
            {isCooked && (
              <VerdictAvatars recipeId={recipe.id} variant="paperDense" />
            )}
          </View>
        </View>

        <View style={styles.proposalControls}>
          <View style={styles.proposalButtons}>
            {canAccept && (
              <Pressable style={styles.putOnMenuBtn} onPress={handleAccept} hitSlop={8}>
                <Text style={styles.putOnMenuText}>{t('household.approval.putOnMenu')}</Text>
              </Pressable>
            )}
            {canRemove && (
              <Pressable style={styles.removeBtn} onPress={handleRemove} hitSlop={8}>
                <Feather name="x" size={14} color={Paprika.textMuted} />
              </Pressable>
            )}
          </View>
          <ApprovalThumbs mealId={meal.id} variant="outline" />
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable style={styles.card} onPress={onPress}>
      <RemoteImage
        uri={recipe.image}
        variant="thumb"
        style={[styles.thumb, thumbStyle]}
        resizeMode="cover"
        testID="secondary-meal-thumb"
        fallback={<View style={[styles.thumb, thumbStyle, styles.thumbPlaceholder]} testID="secondary-meal-thumb" />}
      />

      <View style={styles.info}>
        <Text style={styles.title} numberOfLines={2} testID="planned-meal-title">{title}</Text>
        <View style={styles.metaRow}>
          {recipe.readyInMinutes != null && <TimeChip minutes={recipe.readyInMinutes} />}
          {servings != null && (
            <View style={styles.servings}>
              <Feather name="users" size={11} color={Paprika.textMuted} />
              <Text style={styles.servingsText}>{servings}</Text>
            </View>
          )}
          {isCooked && <Text style={styles.cooked}>{t('thisWeek.cooked')}</Text>}
          {/* Read-only: the compact row has no room for a tap target — tapping
              the row opens the detail sheet, which carries the CTA. */}
          {isCooked && (
            <VerdictAvatars recipeId={recipe.id} variant="paperDense" />
          )}
        </View>
      </View>

      {status === 'paired' ? (
        <ApprovalThumbs mealId={meal.id} variant="outline" />
      ) : (
        <Feather name="chevron-right" size={16} color={Paprika.textMuted} />
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 14,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 5,
    elevation: 1,
  },
  proposalCard: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: Paprika.approvalIdeaTint,
    borderWidth: 1,
    borderStyle: 'dashed' as any,
    borderColor: Paprika.approvalIdea,
    shadowColor: 'rgba(255, 255, 255, 0)',
    shadowOpacity: 0,
    elevation: 0,
  },
  proposalAuthor: {
    fontFamily: 'Inter-Medium',
    fontSize: 8.5,
    color: Paprika.approvalIdea,
    textTransform: 'uppercase' as any,
    letterSpacing: 0.4,
  },
  proposalControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  proposalButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  putOnMenuBtn: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: Paprika.approvalIdea,
  },
  putOnMenuText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 8.5,
    color: Paprika.surfaceLowest,
    textTransform: 'uppercase' as any,
    letterSpacing: 0.3,
  },
  removeBtn: {
    padding: 4,
  },
  thumb: {
    // Dimensions come from tabletMinWidth at render.
    borderRadius: 10,
    backgroundColor: Paprika.bgSunk,
  },
  thumbPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  info: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    lineHeight: 17,
    color: Paprika.textPrimary,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  servings: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  servingsText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textMuted,
  },
  cooked: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.primary,
  },
});

export default SecondaryMealRow;
